import * as React from 'react'
import styled from 'styled-components'
import * as interfaces from 'static/interfaces'
import * as constants from 'static/constants'
import { MemoModal } from './MemoModal'
import LinkableText from 'components/atoms/LinkableText'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

interface IProps {
  target: any
  targetClass: 'tour' | 'tourGroup' | 'otaReservation' | 'user'
  internal: boolean
  external?: boolean
  handleAfterUpdate?: (target: any, params: Record<string, any>) => void
}

const targetNames = {
  tour: 'ツアー',
  tourGroup: 'ツアーグループ',
  otaReservation: 'ゲスト',
  user: 'ガイド',
}

const Memo: React.VFC<IProps> = ({
  target,
  targetClass,
  internal,
  external,
  handleAfterUpdate,
}) => {
  const showModal = React.useCallback(
    e => {
      e.stopPropagation()
      window.globalModal.showModal({
        title: `${targetNames[targetClass]} - メモ入力`,
        body: (
          <MemoModal
            external={external}
            internal={internal}
            target={target}
            handleAfterUpdate={handleAfterUpdate}
            targetClass={targetClass}
          />
        ),
        handleSubmit: undefined,
        zIndex: 6001,
      })
    },
    [targetClass, target, handleAfterUpdate]
  )

  return (
    <Wrapper>
      {internal && target.internal_memo && (
        <div className="memo agentOnly">
          <div aria-label="Agent only note">
            <SvgIcon name="lock_alt" className="icon" ariaHidden={true} role="presentation" />
            エージェント限定
          </div>
          <div className="memoBody">
            <LinkableText linkColor={constants.COLORS.textLink}>
              {target.internal_memo}
            </LinkableText>
          </div>
        </div>
      )}
      {external && target.memo && (
        <div className="memo public">
          <div aria-label="Public note">
            <SvgIcon name="note" className="icon" ariaHidden={true} role="presentation" />
            ガイド公開
          </div>
          <div className="memoBody">
            <LinkableText linkColor={constants.COLORS.textLink}>{target.memo}</LinkableText>
          </div>
        </div>
      )}
      <div className="memoButton" onClick={showModal} role="button" aria-label="Open memo window">
        <SvgIcon name="note" className="icon" ariaHidden={true} role="presentation" />
        メモ
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  // fix webkit line-height bug
  line-height: 1;
  .memo {
    line-height: 16px;
    font-size: 12px;
    padding-top: 8px;
    margin-top: 12px;
    border-top: 1px dashed ${constants.COLORS.divider};
    word-break: break-all;
    div {
      display: flex;
      align-items: center;
      font-size: 10px;
      margin-bottom: 4px;
      .icon {
        width: 16px;
        height: 16px;
        margin-right: 4px;
      }
    }
    &.agentOnly {
      div {
        color: ${constants.COLORS.info2};
      }
    }
    &.public {
      div {
        color: ${constants.COLORS.subText};
      }
    }
    .memoBody {
      // 入力時の改行を反映し、横幅に収まるよう折り返す。2行を超える分は末尾を「…」で省略する
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      width: 100%;
      overflow: hidden;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
  }
  .memoButton {
    display: inline-flex;
    align-items: center;
    line-height: 20px;
    margin-top: 8px;
    color: ${constants.COLORS.primary};
    font-size: 12px;
    transition: color 0.2s;
    vertical-align: middle;
    white-space: nowrap;
    cursor: pointer;
    .icon {
      width: 20px;
      height: 20px;
      margin-right: 4px;
    }
    &:hover {
      color: ${constants.COLORS.secondary};
    }
  }
`

export default Memo
